import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react"
import { useOpenInspector } from "~/components/design-system/pages/internal/InspectorContext"

type InspectorSelectionContextValue = {
  selectedIds: string[]
  setSelectedIds: (ids: string[]) => void
}

const EMPTY: string[] = []

const InspectorSelectionContext = createContext<InspectorSelectionContextValue | null>(null)

export const InspectorSelectionProvider = ({ children }: { children: ReactNode }) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const value = useMemo<InspectorSelectionContextValue>(
    () => ({ selectedIds, setSelectedIds }),
    [selectedIds],
  )
  return (
    <InspectorSelectionContext.Provider value={value}>
      {children}
    </InspectorSelectionContext.Provider>
  )
}

const useInspectorSelectionInternal = () => {
  const ctx = useContext(InspectorSelectionContext)
  if (!ctx) {
    throw new Error("useInspectorSelection must be used inside a <Module>")
  }
  return ctx
}

type UseInspectorSelectionOptions = {
  /** Controlled selection (e.g. fed from a table's row selection). Omit for uncontrolled. */
  value?: string[]
  /** Called whenever the selection changes. Required in controlled mode. */
  onChange?: (ids: string[]) => void
  /** When true, selecting (a non-empty set) also opens the inspector so the detail is visible. */
  openOnSelect?: boolean
}

/**
 * The page's selected item(s), the input driving inspector content. The design system owns
 * selection and open behaviour; the feature owns the CONTENT. Normally a controlled projection of
 * a table's row selection; only usable inside a `<Module>`.
 */
export const useInspectorSelection = (options?: UseInspectorSelectionOptions) => {
  const ctx = useInspectorSelectionInternal()
  const open = useOpenInspector()

  const controlled = options?.value !== undefined
  const selectedIds = controlled ? (options?.value ?? EMPTY) : ctx.selectedIds

  const onChange = options?.onChange
  const openOnSelect = options?.openOnSelect

  const commit = useCallback(
    (next: string[]) => {
      if (controlled) onChange?.(next)
      else ctx.setSelectedIds(next)
      if (openOnSelect && next.length > 0) open()
    },
    [controlled, onChange, ctx, openOnSelect, open],
  )

  const select = useCallback((id: string) => commit([id]), [commit])
  const toggle = useCallback(
    (id: string) =>
      commit(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]),
    [commit, selectedIds],
  )
  const clear = useCallback(() => commit([]), [commit])
  const setSelectedIds = useCallback((ids: string[]) => commit(ids), [commit])
  const isSelected = useCallback((id: string) => selectedIds.includes(id), [selectedIds])

  return {
    selectedIds,
    selectedId: selectedIds[0] ?? null,
    isSelected,
    select,
    toggle,
    clear,
    setSelectedIds,
  }
}
